import { Field } from "react-final-form";
import cn from "classnames";
import { useEffect, useRef } from "react";
import styles from "./ErrorField.module.scss";
import { sanitizeData } from "~/utils";
import { isValidElement } from "react";
import { ERROR_FIELD_ } from "~/configs/consts";
import { message } from "antd";

export type RenderError = {
  has: 1 | 0;
  message: string;
  color?: string;
};

interface ErrorFieldProps {
  name: string;
  renderError?: RenderError | null;
  classNameContainer?: string;
  descriptionField?: string | React.ReactNode;
}

interface ErrorFieldContentProps {
  name: string;
  renderError?: RenderError | null;
  classNameContainer?: string;
  descriptionField?: string | React.ReactNode;
  submitFailed?: boolean;
  error: any;
  submitError: any;
}

const ErrorFieldContent = ({
  name,
  renderError,
  classNameContainer,
  descriptionField,
  submitFailed,
  error,
  submitError
}: ErrorFieldContentProps) => {
  const prevIsError = useRef(false);

  const isError = ((error || submitError) && submitFailed) || (renderError && renderError.has);
  const errorMessage = renderError?.message || error || submitError;

  const isBackendError = Boolean(submitError && submitFailed);

  const errorId = isError && isBackendError ? `${ERROR_FIELD_}${name}-error` : undefined;

  useEffect(() => {
    if (isError && !prevIsError.current && errorId) {
      const el = document.getElementById(errorId);

      if (el && typeof el.scrollIntoView === "function") {
        try {
          message.destroy();
          message.error({
            content:
              "Пожалуйста, исправьте ошибки и заполните все обязательные поля для продолжения",
            duration: 3
          });

          el.scrollIntoView({ behavior: "smooth", block: "center" });

          const timeout = setTimeout(() => {
            if (el && "focus" in el) {
              try {
                (el as HTMLElement).focus({ preventScroll: true });
              } catch {}
            }
          }, 600);

          return () => clearTimeout(timeout);
        } catch {}
      }
    }
    prevIsError.current = isError;
  }, [isError, errorId]);

  return (
    <div className={cn(styles.container, classNameContainer)}>
      {isError && (
        <p
          id={errorId}
          className={styles.text}
          style={{
            color: renderError?.color,
            display: "flex",
            alignItems: "flex-start",
            flexDirection: typeof errorMessage === "string" ? "column" : "row"
          }}
        >
          {typeof errorMessage === "string"
            ? errorMessage.split("\n").map((line, index) => (
                <span key={index}>
                  {sanitizeData(line)}
                  <br />
                </span>
              ))
            : isValidElement(errorMessage)
            ? errorMessage
            : errorMessage
            ? sanitizeData(String(errorMessage))
            : null}
        </p>
      )}
      {!isError && descriptionField && <p className={styles.textDescription}>{descriptionField}</p>}
    </div>
  );
};

export const ErrorField = ({
  name,
  renderError,
  classNameContainer,
  descriptionField
}: ErrorFieldProps) => {
  return (
    <Field
      name={name}
      subscription={{ submitFailed: true, error: true, submitError: true }}
      render={({ meta: { submitFailed, error, submitError } }) => (
        <ErrorFieldContent
          name={name}
          renderError={renderError}
          classNameContainer={classNameContainer}
          descriptionField={descriptionField}
          submitFailed={submitFailed}
          error={error}
          submitError={submitError}
        />
      )}
    />
  );
};
